/**
 * `?next=` deep-link target.
 *
 * Set by the reception QR codes (/login?h=CODE&next=checkin|checkout)
 * and by state-aware guest links in emails (/booking/<token>?next=…).
 * After authentication — or immediately, when the guest already has a
 * session — the app lands on that flow instead of the home screen.
 *
 * The flow screens themselves still enforce their own rules (feature
 * enabled, date reached, booking state), so an out-of-context target
 * degrades to the home screen rather than a broken page.
 */
const ALLOWED = ['checkin', 'checkout', 'pay', 'home', 'hotel-info'] as const;

export type NextTarget = typeof ALLOWED[number];

/** Reads and validates `next` from a query string (defaults to the current URL). */
export const readNextTarget = (search?: string): NextTarget | null => {
  const raw = new URLSearchParams(search ?? window.location.search).get('next');
  if (!raw) return null;
  const value = raw.replace(/^\//, '').toLowerCase();
  return (ALLOWED as readonly string[]).includes(value) ? (value as NextTarget) : null;
};

/** Route to navigate to after auth: the `next` target, else the home screen. */
export const routeAfterAuth = (search?: string): string => {
  const target = readNextTarget(search);
  return target ? `/${target}` : '/home';
};
